import React, { useEffect, useState } from "react";
import { Inbox, MessageSquareText, RefreshCcw } from "lucide-react";
import { API_BASE_URL } from "../api";
import { Button, MetricCard, SectionHeader, SurfaceCard } from "../components/designSystem";

function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadMessages = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Admin access required.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    fetch(`${API_BASE_URL}/api/messages`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setMessages(data);
        } else {
          setError(data.error || "Failed to fetch messages.");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Network error.");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadMessages();
  }, []);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(244,114,182,0.14),_transparent_22%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(244,114,182,0.12),_transparent_22%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Admin Messages"
          title="Contact form inbox"
          description="Review all messages submitted through the contact form in one admin queue."
          action={
            <Button type="button" variant="secondary" onClick={loadMessages}>
              <RefreshCcw className="h-4 w-4" />
              Refresh
            </Button>
          }
        />
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <MetricCard label="Messages" value={String(messages.length)} helper="Messages currently visible in the inbox." />
          <MetricCard label="Status" value={loading ? "Loading" : "Ready"} helper="Current fetch state for the inbox." />
          <MetricCard label="Channel" value="Contact form" helper="Entries submitted from the public contact workflow." />
        </div>
      </SurfaceCard>

      {error ? <SurfaceCard><p className="text-sm font-medium text-rose-600 dark:text-rose-300" role="alert">{error}</p></SurfaceCard> : null}

      {loading ? (
        <SurfaceCard>
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading messages...</p>
        </SurfaceCard>
      ) : messages.length === 0 ? (
        <SurfaceCard>
          <p className="text-sm text-slate-500 dark:text-slate-400">No messages found.</p>
        </SurfaceCard>
      ) : (
        <div className="space-y-4">
          {messages.map((entry) => (
            <SurfaceCard key={entry._id}>
              <div className="flex items-start gap-4">
                <div className="rounded-2xl bg-slate-100 p-3 text-slate-900 dark:bg-gray-900 dark:text-white">
                  <Inbox className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-950 dark:text-white">{entry.name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{entry.email}</p>
                    </div>
                    <div className="inline-flex items-center gap-2 text-xs text-slate-400">
                      <MessageSquareText className="h-3.5 w-3.5" />
                      {entry.created_at ? new Date(entry.created_at).toLocaleString() : "No date"}
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">{entry.message}</p>
                </div>
              </div>
            </SurfaceCard>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminMessages;
